/**
 * Normalizes and hashes an email address using SHA-256.
 *
 * Uses Web Crypto API (crypto.subtle) for browser and Electron environments.
 * For Electron desktop app, the Web Crypto API is fully supported since Electron uses Chromium.
 *
 * **Security Context Requirement:**
 * The Web Crypto API requires a secure context (HTTPS, localhost, or 127.0.0.1).
 * When accessed via HTTP on non-localhost domains, crypto.subtle will be undefined
 * and this function will gracefully return undefined (tracking events will fire without the em parameter).
 *
 * **Email Canonicalization:**
 * The email address is canonicalized before hashing:
 * 1. Lowercase the address
 * 2. Remove email alias (strip everything between first + and @)
 * 3. Remove special characters (non-alphanumeric) from the username
 * 4. Hash using SHA-256
 *
 * @param email - The email address to hash
 * @returns SHA-256 hash as lowercase hex string (64 characters), or undefined if email is invalid or hashing fails
 *
 * @example
 *   hashEmail('AL.ice$+AppLe@Example.Com') // 'ff8d9819fc0e12bf0d24892e45987e249a28dce836a85cad60e28eaaa8c6d976'
 */
export async function hashEmail(email: string | null | undefined): Promise<string | undefined> {
  if (!email || typeof email !== 'string') {
    return undefined;
  }

  try {
    // Step 1: Lowercase the address
    const lowercaseEmail = email.trim().toLowerCase();

    if (!lowercaseEmail || !lowercaseEmail.includes('@')) {
      return undefined;
    }

    // Validate email format: must have exactly one @ symbol
    const atCount = (lowercaseEmail.match(/@/g) || []).length;
    if (atCount !== 1) {
      return undefined;
    }

    // Split into username and domain
    const atIndex = lowercaseEmail.indexOf('@');
    let username = lowercaseEmail.substring(0, atIndex);
    const domain = lowercaseEmail.substring(atIndex + 1);

    // Validate that both username and domain exist
    if (!username || !domain) {
      return undefined;
    }

    // Step 2: Remove email alias (strip everything between first + and @)
    const plusIndex = username.indexOf('+');
    if (plusIndex !== -1) {
      username = username.substring(0, plusIndex);
    }

    // Step 3: Remove special characters (non-alphanumeric) from the username
    username = username.replace(/[^a-z0-9]/g, '');

    // If username is empty after canonicalization, return undefined
    if (!username) {
      return undefined;
    }

    // Reconstruct canonicalized email
    const normalizedEmail = username + '@' + domain;

    if (typeof crypto === 'undefined' || !crypto.subtle) {
      // eslint-disable-next-line no-console
      console.warn(
        `[hashEmail] Web Crypto API not available. crypto.subtle requires HTTPS or localhost. Email tracking parameter will be omitted.`,
      );

      return undefined;
    }

    // Convert string to bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedEmail);

    // Hash using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    // Graceful degradation - don't break tracking
    // This catches any unexpected errors during hashing
    return undefined;
  }
}
